import Purchase16 from '@carbon/icons-react/es/purchase/16';
import { Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { FC, useCallback, useContext, useMemo, useState } from 'react';
import {
  ProjectFinancialsContractType,
  FinancialsProjectSettings,
} from 'api/project';
import { ProjectFinancialsSettingsContext } from 'contexts/project-financials-settings';
import { useTranslate } from 'hooks/use-translation';
import { useAccountProfile } from 'queries/account';
import { EditBudgetAndRateInfo, EditBudgetAndRateTypes } from './edit-drawer';
import {
  BillingInfoTable,
  EditIcon,
  InfoWrapper,
  RateCardInfo,
  Wrapper,
} from './styles';

type BillingInfoRowData = {
  name: string;
  value: string | JSX.Element;
  openDrawer: (type: EditBudgetAndRateTypes) => void;
  type: EditBudgetAndRateTypes;
};

const ValueRenderer = ({
  data,
  canUpdate,
}: {
  data: BillingInfoRowData;
  canUpdate: boolean | undefined;
}) => {
  const translate = useTranslate();
  const { openDrawer, type } = data;
  const openWithEditType = () => openDrawer(type);

  return (
    <Wrapper>
      {canUpdate ? (
        <Tooltip title={translate('edit')}>
          <EditIcon onClick={openWithEditType} />
        </Tooltip>
      ) : null}
      {data.value}
    </Wrapper>
  );
};

const BillRateInfoRenderer = ({
  data,
}: {
  data: FinancialsProjectSettings;
}) => {
  const translate = useTranslate();

  if (data.rateCard) {
    return (
      <RateCardInfo>
        <Purchase16 /> {data.rateCard?.rateCardName || ''}
      </RateCardInfo>
    );
  }

  if (!data.rateCard && data.projectCustomRates.length) {
    return (
      <InfoWrapper>
        {translate('project.settings.budget.rate.manually_set')}
      </InfoWrapper>
    );
  }

  return (
    <InfoWrapper>
      {translate('project.settings.budget.rate.no_rate_cards')}
    </InfoWrapper>
  );
};

export const BillingInfo: FC<{
  projectId: number;
  projectCurrencySymbol: string | undefined;
  data: FinancialsProjectSettings;
}> = ({ data, projectId, projectCurrencySymbol }) => {
  const { permissionsMap } = useContext(ProjectFinancialsSettingsContext);
  const { data: account } = useAccountProfile();
  const {
    projectBudget: { canWrite: canUpdateBudget },
    projectHours: { canWrite: canUpdateHours },
    projectRateCardUpdate: { canWrite: canUpdateRateCard },
  } = permissionsMap;

  const translate = useTranslate();
  const [isDrawerVisible, setDrawerVisiblity] = useState(false);
  const [editType, setEditType] =
    useState<EditBudgetAndRateTypes | undefined>();
  const openDrawer = (type: EditBudgetAndRateTypes) => {
    setEditType(type);
    setDrawerVisiblity(true);
  };

  const checkUpdatePermissionsForField = useCallback(
    (type: EditBudgetAndRateTypes) => {
      switch (type) {
        case EditBudgetAndRateTypes.FixedFee:
        case EditBudgetAndRateTypes.ProjectBudget:
          return canUpdateBudget;
        case EditBudgetAndRateTypes.BillRate:
          return canUpdateRateCard;
        case EditBudgetAndRateTypes.ProjectHours:
          return canUpdateHours;
        default:
          return false;
      }
    },
    [canUpdateBudget, canUpdateHours, canUpdateRateCard],
  );
  const rowData = useMemo(
    () =>
      [
        {
          name:
            data.financialContractType === ProjectFinancialsContractType.FIXED
              ? translate(
                  `project.settings.budget.rate.${ProjectFinancialsContractType.FIXED}.title`,
                )
              : translate(
                  `project.settings.budget.rate.${ProjectFinancialsContractType.TM}.title`,
                ),
          value: `${
            projectCurrencySymbol ?? account?.currency?.currencySymbol ?? ''
          } ${data.projectBudget ?? 0}`.replace(/\B(?=(\d{3})+(?!\d))/g, ','),
          type:
            data.financialContractType === ProjectFinancialsContractType.FIXED
              ? EditBudgetAndRateTypes.FixedFee
              : EditBudgetAndRateTypes.ProjectBudget,
          openDrawer,
        },
        {
          name: translate('project.financials.project_hours'),
          value: `${data.projectHours ?? 0}h`,
          type: EditBudgetAndRateTypes.ProjectHours,
          openDrawer,
        },
        {
          name: translate('project.settings.budget.rate.bill_rate.title'),
          value: <BillRateInfoRenderer data={data} />,
          type: EditBudgetAndRateTypes.BillRate,
          openDrawer,
        },
      ].filter((entry) =>
        data.financialContractType !== ProjectFinancialsContractType.NONBILLABLE
          ? true
          : entry.type === EditBudgetAndRateTypes.ProjectHours,
      ),
    [account?.currency.currencySymbol, data, projectCurrencySymbol, translate],
  );

  const columns: ColumnsType = useMemo(
    () => [
      {
        title: translate('project.settings.budget.rate.table.team_members'),
        key: 'name',
        dataIndex: 'name',
        width: '40%',
      },
      {
        title: translate('project.settings.budget.rate.table.role'),
        key: 'value',
        width: '60%',
        render: (record) => {
          const { type } = record;

          return (
            <ValueRenderer
              data={record}
              canUpdate={checkUpdatePermissionsForField(type)}
            />
          );
        },
      },
    ],
    [checkUpdatePermissionsForField, translate],
  );

  return (
    <>
      <BillingInfoTable
        dataSource={rowData}
        columns={columns}
        showHeader={false}
        pagination={false}
      />
      <EditBudgetAndRateInfo
        projectId={projectId}
        visible={isDrawerVisible}
        setVisiblity={setDrawerVisiblity}
        type={editType}
      />
    </>
  );
};
